import { format, differenceInSeconds } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom'
import { fetchProfile } from 'src/helper/client';
import useGetProfile from './useGetProfile';
import mixpanel from "mixpanel-browser";
import { saveSessionLog } from 'src/utils/sessionLogsDB';
import { getGroup } from 'src/helper/app-user-tracking';

const useMixPanelTracker = () => {

  const location = useLocation();
  const analyticsRef = useRef(false)
  const mixpanelInitializedRef = useRef(false)
  const initRetryCountRef = useRef(0)
  const MAX_RETRY_ATTEMPTS = 1
  const RETRY_DELAY = 1000 // 1 second
  const User = JSON.parse(localStorage.getItem('User'));
  const [getProfile] = useGetProfile();
  const sessionRef = useRef(null)
  const sessionUrlRef = useRef(null)
  const sessionStartTimeRef = useRef(null)
  const lastActiveTimeRef = useRef(null)
  const totalActiveTimeRef = useRef(0)
  const isActiveRef = useRef(false)
  const activityCheckIntervalRef = useRef(null)
  const networkStatusRef = useRef(navigator.onLine)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  
  const initializeMixpanel = () => {
    try {
      if (process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
        mixpanel.init(process?.env?.REACT_APP_MIXPANEL_TOKEN, {
          debug: true,
          track_pageview: true,
          persistence: "localStorage",
        });
        
        if (User?.email) {
          mixpanel.identify(User.email);
          mixpanel.people.set({
            name: User.fullName,
            email: User.email,
            $email: User.email,
            $name: User.fullName,
          });
        }
        mixpanelInitializedRef.current = true;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
      return false;
    }
  };

  const retryInitialization = () => {
    if (initRetryCountRef.current < MAX_RETRY_ATTEMPTS) {
      setTimeout(() => {
        initRetryCountRef.current += 1;
        if (!initializeMixpanel()) {
          retryInitialization();
        }
      }, RETRY_DELAY);
    } else {
      console.error('Failed to initialize Mixpanel after maximum retry attempts');
    }
  };

  const isMixpanelInitialized = () => {
    try {
      if (mixpanel && mixpanel.hasOwnProperty('__loaded') && mixpanel.__loaded === true) {
        return true;
      }
      
      // If not initialized, attempt to initialize
      if (!mixpanelInitializedRef.current) {
        if (!initializeMixpanel()) {
          retryInitialization();
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking Mixpanel initialization:', error);
      return false;
    }
  };

  const generateUTCtimestamp = () => {
    const currentDate = new Date();
    return currentDate.toISOString();
  };

  const getSessionDuration = () => {
    if (!sessionStartTimeRef.current || !lastActiveTimeRef.current) return 0;
    return differenceInSeconds(new Date(lastActiveTimeRef.current), new Date(sessionStartTimeRef.current));
  };

  const sendSessionLog = async (reason = 'normal') => {
    if (!sessionStartTimeRef.current || !lastActiveTimeRef.current) return;

    const sessionDuration = getSessionDuration();
    if (sessionDuration <= 0) return;

    try {
      let reportTitle = getReportPage(location.pathname);
      if(document && document?.title) {
        document.title = reportTitle
      }
      const properties = {
        userId: String(User.email),
        profileName: getProfile.accountInfo.name,
        profileId: getProfile.profileId,
        type: 'session-logs',
        reportPage: reportTitle,
        utcTime: generateUTCtimestamp(),
        role: getProfile?.accessLevel || 'Admin',
        companyName: getProfile?.companyName ? getProfile?.companyName : User?.companyName ? User?.companyName : getBrandProvider(getProfile?.accountInfo?.name),
        sessionId: sessionRef.current,
        userName: User.fullName,
        clientEmail: getProfile?.client_email ?? getProfile?.clientEmail ?? User.email,
        path: location.pathname,
        $email: User.email,
        $name: User.fullName,
        sessionStartTime: sessionStartTimeRef.current,
        sessionEndTime: lastActiveTimeRef.current,
        sessionDuration: sessionDuration,
        totalActiveTime: totalActiveTimeRef.current,
        endReason: reason,
        networkStatus: navigator.onLine ? 'online' : 'offline'
      };

      // Send to Mixpanel if enabled
      if (process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
        console.log("Session-Logs-Testing", {properties});
        
        mixpanel.track('session-logs', properties);
      }

      // Save to IndexedDB
      try {
        await saveSessionLog(properties);
      } catch (error) {
        console.error('Error saving session log to IndexedDB:', error);
        // Retry once after a short delay
        setTimeout(async () => {
          try {
            await saveSessionLog(properties);
          } catch (retryError) {
            console.error('Error retrying session log save to IndexedDB:', retryError);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error in sendSessionLog:', error);
    }
  };

  // Disabled: only `session-logs` is sent to Mixpanel (see sendSessionLog). Re-enable to track start/stop viewing again.
  // const sendEvent = (category, label, value) => {
  //   if(process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
  //     try {
  //       if (!isMixpanelInitialized()) {
  //         setTimeout(() => sendEvent(category, label, value), RETRY_DELAY);
  //         return;
  //       }

  //       const properties = {
  //         userId: String(User.email),
  //         profileName: getProfile.accountInfo.name,
  //         profileId: getProfile.profileId,
  //         type: category,
  //         reportPage: window.location.href.split("/")[window.location.href.split("/").length - 1].replace(/\b\w/g, (match) => match.toUpperCase()),
  //         utcTime: generateUTCtimestamp(),
  //         role: getProfile?.accessLevel || 'Admin',
  //         sessionId: sessionRef.current,
  //         userName: User.fullName,
  //         path: location.pathname,
  //         value: value,
  //         $email: User.email,
  //         $name: User.fullName,
  //         sessionStartTime: sessionStartTimeRef.current,
  //         lastActiveTime: lastActiveTimeRef.current,
  //         totalActiveTime: totalActiveTimeRef.current,
  //         companyName: getProfile?.companyName ? getProfile?.companyName : User?.companyName ? User?.companyName : getBrandProvider(getProfile?.accountInfo?.name),
  //       };

  //       mixpanel.track(category, {
  //         ...properties,
  //         event_label: label,
  //       });

  //     } catch (error) {
  //       console.log("sendEventError", { error });
  //     }
  //   }
  // };

  const updateActiveTime = () => {
    if (isActiveRef.current && lastActiveTimeRef.current) {
      const now = new Date();
      const timeDiff = differenceInSeconds(now, new Date(lastActiveTimeRef.current));
      if (timeDiff > 0 && timeDiff < 60) { // Only count if difference is less than 60 seconds
        totalActiveTimeRef.current += timeDiff;
      }
      lastActiveTimeRef.current = now.toISOString();
    }
  };

  const startWatching = () => {
    if (!isMixpanelInitialized()) {
      console.warn('Mixpanel is not initialized. Cannot start watching.');
      return;
    }

    if (getProfile && getProfile?.profileId && User && User?.email && !analyticsRef.current && process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
      const now = new Date();
      sessionRef.current = `${format(now, 'yyyyMMddHHmmss')}`;
      sessionUrlRef.current = window.location.href;
      sessionStartTimeRef.current = now.toISOString();
      lastActiveTimeRef.current = now.toISOString();
      isActiveRef.current = true;
      
      // Initialize mixpanel with user identity
      mixpanel.identify(User.email);
      mixpanel.people.set({
        profileId: getProfile.profileId,
        role: getProfile?.accessLevel || 'Admin',
        userName: User.fullName,
        name: User.fullName,
        email: User.email,
        $email: User.email,
        $name: User.fullName,
      });

      // const profileValues = {
      //   profileId: getProfile.profileId,
      //   role: getProfile?.accessLevel || 'Admin',
      //   userName: User.fullName,
      //   name: User.fullName,
      //   email: User.email,
      //   $email: User.email,
      //   $name: User.fullName,
      // };

      // sendEvent('start-viewing', String(User.email), JSON.stringify(profileValues));
      analyticsRef.current = true;

      // Start activity check interval
      activityCheckIntervalRef.current = setInterval(updateActiveTime, 5000); // Check every 5 seconds
    }
  };

  const stopWatching = (reason = 'normal') => {
    if (getProfile && getProfile?.profileId && User && User?.email && analyticsRef.current && sessionRef.current && process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
      isActiveRef.current = false;
      lastActiveTimeRef.current = new Date().toISOString();
      
      // Clear activity check interval
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
        activityCheckIntervalRef.current = null;
      }

      // const profileValues = {
      //   profileId: getProfile.profileId,
      //   role: getProfile?.accessLevel || 'Admin',
      //   userName: User.fullName,
      //   name: User.fullName,
      //   email: User.email,
      //   $email: User.email,
      //   $name: User.fullName,
      // };

      // sendEvent('stop-viewing', String(User.email), JSON.stringify({
      //   ...profileValues,
      //   reason: reason
      // }));
      sendSessionLog(reason);
      analyticsRef.current = false;

      // Clear session data
      sessionStartTimeRef.current = null;
      totalActiveTimeRef.current = 0;
    }
  };

  const handleNetworkChange = () => {
    const wasOnline = networkStatusRef.current;
    const isCurrentlyOnline = navigator.onLine;
    networkStatusRef.current = isCurrentlyOnline;
    setIsOnline(isCurrentlyOnline);

    if (wasOnline && !isCurrentlyOnline) {
      // Network went offline
      stopWatching('network_disconnected');
    } else if (!wasOnline && isCurrentlyOnline) {
      // Network came back online - start a new session
      startWatching();
    }
  };

  useEffect(() => {
    async function getReportTitle() {
      const reportTitle = getReportPage(String(location.pathname));
      if(reportTitle && document && document?.title) {
        document.title = reportTitle
      }
    }
    getReportTitle()
  }, [location.pathname])


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        stopWatching('tab_hidden');
      } else {
        startWatching();
      }
    };

    const handleFocus = () => {
      if (!analyticsRef.current) {
        startWatching();
      }
      isActiveRef.current = true;
    };

    const handleBlur = () => {
      isActiveRef.current = false;
      stopWatching('tab_blur');
    };

    const handleBeforeUnload = (event) => {
      stopWatching('page_unload');
    };

    const handlePageHide = (event) => {
      // Handle both normal navigation and hard refresh
      stopWatching(event.persisted ? 'page_refresh' : 'page_hide');
    };

    if(process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
      if(getProfile && getProfile?.profileId && getProfile?.accountInfo && User && User?.email) {
        startWatching();
        
        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('online', handleNetworkChange);
        window.addEventListener('offline', handleNetworkChange);
      }
    }

    return () => {
      if(getProfile && getProfile?.profileId && getProfile?.accountInfo && User && User?.email && process?.env?.REACT_APP_MIXPANEL_TOKEN && process?.env?.REACT_APP_MIX_PANEL_ENABLE == 1) {
        stopWatching('component_unmount');
        analyticsRef.current = false;

        // Remove event listeners
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('online', handleNetworkChange);
        window.removeEventListener('offline', handleNetworkChange);
        
        if (activityCheckIntervalRef.current) {
          clearInterval(activityCheckIntervalRef.current);
        }
      }
    };
  }, [location.pathname, getProfile?.profileId]);

  return {
    analyticsRef,
    sessionStartTime: sessionStartTimeRef.current,
    totalActiveTime: totalActiveTimeRef.current,
    isOnline
  }

};

export const getReportPage = (pathname) => {
  // Remove trailing slash if exists
  const path = pathname.replace(/\/$/, '');
  
  // Special cases mapping
  const specialRoutes = {
    '/summary/account': 'Account Summary',
    '/summary/all-campaigns': 'All Campaigns Summary',
    '/summary/flipkart': 'Flipkart Dashboard',
    '/amazon-dsp': 'Amazon DSP',
    '/amazon-dsp-request': 'Amazon DSP Request',
    '/amazon-dsp-request/create': 'Create Amazon DSP Request',
    '/amazon-snapshot': 'Amazon Snapshot',
    '/flipkart-snapshot': 'Flipkart Snapshot',
    '/amazon-master-overview': 'Amazon Master Overview',
    '/flipkart-master-overview': 'Flipkart Master Overview',
    '/amazon-dayparting-performance': 'Amazon Dayparting Performance',
    '/amazon-bid-scheduler': 'Amazon Bid Scheduler',
    '/amazon-dayparting-scheduler': 'Amazon Dayparting Scheduler',
    '/amazon-dayparting-history': 'Amazon Dayparting History',
    '/amazon-user-management': 'Amazon User Management',
    '/flipkart-user-management': 'Flipkart User Management',
    '/instamart-user-management': 'Instamart User Management',
    '/myntra-user-management': 'Myntra User Management',
    '/blinkit-user-management': 'Blinkit User Management',
    '/amazon-hector-sov': 'Amazon SOV',
    '/amazon/marketing-stream': 'Amazon Marketing Stream',
    '/amazon/targeting': 'Amazon Targeting 360',
    '/summary/myntra': 'Myntra Targeting 360',
    '/summary/flipkart': 'Flipkart Targeting 360',
    '/summary/instamart': 'Instamart Targeting 360',
    '/summary/blinkit': 'Blinkit Targeting 360',
    '/amazon-text-categorization': 'Amazon Text Categorization',
    '/amazon-dsp-text-categorization': 'Amazon DSP Text Categorization',
    '/flipkart-text-categorization': 'Flipkart Text Categorization',
    '/instamart-text-categorization': 'Instamart Text Categorization',
    '/myntra-text-categorization': 'Myntra Text Categorization',
    '/blinkit-text-categorization': 'Blinkit Text Categorization',
    '/amazon-audience-report': 'Amazon Audience Report',
    '/amazon-asin-audience-report': 'Amazon ASIN Audience Report',
    '/amazon-create-audience': 'Create Amazon Audience',
    '/amazon-chatgpt': 'Amazon ChatGPT',
    '/amazon-insights': 'Amazon Insights',
    '/amazon-path-to-conversion': 'Amazon Path to Conversion',
    '/amazon-custom-attribution': 'Amazon Custom Attribution',
    '/amazon-budget-rules-creation': 'Amazon Budget Rules Creation',
    '/amazon-history': 'Amazon History',
    '/amazon-audience-instance': 'Amazon Audience Instance',
    '/session-logs': 'Session Logs',
    '/account-settings': 'Account Settings',
    '/account-management': 'Account Management',
    '/account-manager': 'Account Manager',
    '/profile': 'Profile',
    '/users': 'Users Management',
    '/billing': 'Billing',
    '/billing-payments': 'Billing Payments',
    '/custom/payments': 'Custom Payments',
    '/cpc/history': 'CPC History',
    '/rules': 'Rules',
    '/blacklist': 'Blacklist',
    '/heat-map': 'Heat Map',
    '/keyword-harvesting': 'Keyword Harvesting',
    '/admin/useraccountdetails': 'User Account Details',
    '/admin/client-access-details': 'Client Access Details',
    '/admin/coupons': 'Coupons Management',
    '/admin/manual-transaction-details': 'Manual Transaction Details',
    '/settings/amazon': 'Amazon Settings',
    '/amazon-blacklist-keywords': 'Amazon Blacklist Keywords',
    '/dsp-credit-top-up': 'DSP Credit Top Up',
    '/dsp-wallet': 'DSP Wallet',
    '/dsp-payment': 'DSP Payment',
    '/dsp-invoice-history': 'DSP Invoice History',
    '/dsp-payment-confirmation': 'DSP Payment Confirmation',
    '/summary/zepto': 'Zepto Targeting 360',
    '/zepto-user-management': 'Zepto User Management',
    '/zepto-text-categorization': 'Zepto Text Categorization',
    '/summary/bigbasket': 'Bigbasket Targeting 360',
    '/bigbasket-user-management': 'Bigbasket User Management',
    '/bigbasket-text-categorization': 'Bigbasket Text Categorization',
    '/amazon/smart-campaigns': 'Amazon Smart Campaigns',
    '/amazon/campaign-drafts': 'Amazon Auto Campaign Drafts',
    '/amazon/smart-campaigns/auto/create': 'Amazon Auto Campaign Drafts Creation',
    '/amazon/smart-campaigns/keyword-harvesting/create': 'Amazon Keyword Harvesting Campaign Drafts Creation',
    '/amazon/search-query-performance': 'Amazon Search Query Performance',
    '/amazon/sqp-text-categorization': 'SQP Text Categorization',
    '/genai/openchat/campaign': 'Amazon Campaign GenAI Smart Breakdown',
    '/genai/openchat/targeting': 'Amazon Targeting GenAI Smart Breakdown',
    '/genai/openchat/subtargeting': 'Amazon SubTargeting GenAI Smart Breakdown',
    '/genai/openchat/placement': 'Amazon Placement GenAI Smart Breakdown',
    '/genai/openchat/matchtype': 'Amazon MatchType GenAI Smart Breakdown',
    '/genai/openchat/advertised-asins': 'Amazon AdvertisedAsins GenAI Smart Breakdown',
    '/genai/openchat/targets': 'Amazon Targets GenAI Smart Breakdown',
    '/genai/openchat/search-term': 'Amazon SearchTerm GenAI Smart Breakdown',
    '/genai/genai': 'GenAI Smart Breakdown',
  };

  // Check for special routes first
  if (specialRoutes[path]) {
    return specialRoutes[path];
  }

  // Handle dynamic routes
  if (path.startsWith('/summary/')) {
    const reportId = path.split('/')[2];
    return `${reportId.charAt(0).toUpperCase() + reportId.slice(1)} Summary`;
  }

  if(path.startsWith('/amazon/targeting/')) {
    return 'Amazon Targeting - Asin Details';
  }

  if (path.startsWith('/amazon-automated-rules/')) {
    const parts = path.split('/');
    const ruleType = parts[2]; // bid, budget, etc.
    
    // Handle different URL patterns
    if (parts.length >= 4) {
      if (parts[3] === 'create') {
        return `Create Amazon Automated ${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} Rule`;
      }
      if (parts[3] === 'view-logs-notifications') {
        return `Amazon Automated ${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} Rule Logs - Notification`;
      }
      if (parts[3] === 'view-logs') {
        return `Amazon Automated ${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} Rule Logs`;
      }
      if (parts[3] === 'details') {
        return `Amazon Automated ${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} Rule Details`;
      }
    }
    
    // Map common rule types
    const ruleTypeMap = {
      'bid': 'Bid',
      'budget': 'Budget',
      'status': 'Status',
      'placement': 'Placement',
      'searchterm-negation': 'Search Term Negation',
      'searchterm-harvesting': 'Search Term Harvesting'
    };

    const formattedType = ruleTypeMap[ruleType] || 
      ruleType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    
    return `Amazon Automated ${formattedType} Rules`;
  }

  if (path.startsWith('/amazon-view-audience/')) {
    return 'Amazon View Audience';
  }

  return 'Dashboard'; // Default fallback
};

const getBrandProvider = (profileName) => {
  return getGroup(profileName) || profileName
}; 

export default useMixPanelTracker;
